// firebase/lmt/src/components/CatalogBeta/hooks/useCatalogSearchResults.ts

import { type ApiClient, useApiClient } from "@albertine/api-client";
import {
    type CatalogItemSearchResult,
    type SearchCatalogItemsQuery,
    type SearchCatalogItemsResponseData,
    SuccessResponseSearchCatalogItemsSchema,
} from "@albertine/types/openapi/lmt";
import * as Sentry from "@sentry/react";
import {
    type InfiniteData,
    type UseInfiniteQueryResult,
    useInfiniteQuery,
} from "@tanstack/react-query";
import { toCatalogItemsSearchGetParams } from "@/common/CatalogItem";

export const CATALOG_SEARCH_RESULTS_PAGE_LENGTH = 500;

export type CatalogSearchResultsPage = {
    data: CatalogItemSearchResult[];
    total: number;
    cursor?: string;
};

export function catalogSearchResultsQueryKey(filter: SearchCatalogItemsQuery) {
    return ["catalog-search-results", filter];
}

export function getCatalogSearchResultsItems(
    data: InfiniteData<CatalogSearchResultsPage> | undefined,
): CatalogItemSearchResult[] {
    return data?.pages.flatMap((page) => page.data) ?? [];
}

export function getCatalogSearchResultsTotal(
    data: InfiniteData<CatalogSearchResultsPage> | undefined,
): number {
    return data?.pages[0]?.total ?? 0;
}

export function useCatalogSearchResults(
    filter: SearchCatalogItemsQuery,
    options?: {
        enabled?: boolean;
        pageLength?: number;
    },
): UseInfiniteQueryResult<InfiniteData<CatalogSearchResultsPage>, Error> {
    const { apiClient } = useApiClient();
    const pageLength = options?.pageLength ?? CATALOG_SEARCH_RESULTS_PAGE_LENGTH;
    const enabled = options?.enabled ?? false;

    return useInfiniteQuery({
        queryKey: catalogSearchResultsQueryKey(filter),
        queryFn: async ({ pageParam }) => {
            return queryCatalogSearchResultsPage(
                apiClient,
                filter,
                pageLength,
                pageParam,
            );
        },
        enabled,
        getNextPageParam: (lastPage, _allPages) => {
            return lastPage.cursor;
        },
        initialPageParam: undefined as string | undefined,
        staleTime: 0,
    });
}

export async function queryCatalogSearchResultsPage(
    apiClient: ApiClient,
    filter: SearchCatalogItemsQuery,
    pageLength: number,
    page: string | undefined,
): Promise<CatalogSearchResultsPage> {
    const query: SearchCatalogItemsQuery = {
        ...filter,
        pageSize: pageLength,
        cursor: page,
    };

    const response = await queryCatalogItemsSearch(apiClient, query);

    return {
        data: response.catalogItems,
        total: response.totalItems,
        cursor: response.nextPageCursor ?? undefined,
    };
}

async function queryCatalogItemsSearch(
    apiClient: ApiClient,
    filter: SearchCatalogItemsQuery,
): Promise<SearchCatalogItemsResponseData> {
    try {
        const response = await apiClient.get.catalogItemsSearch(
            toCatalogItemsSearchGetParams(filter),
        );
        const responseJson: unknown = await response.json();
        const parsed = SuccessResponseSearchCatalogItemsSchema.safeParse(responseJson);

        if (parsed.success) {
            return parsed.data.data;
        }

        Sentry.captureException(parsed.error, {
            extra: { message: "Failed to parse catalog items search response" },
        });
        return { catalogItems: [], totalItems: 0, nextPageCursor: null };
    } catch (error) {
        Sentry.captureException(error, {
            extra: { message: "Failed to load catalog items" },
        });
        return { catalogItems: [], totalItems: 0, nextPageCursor: null };
    }
}
